import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

// ─── Thunks ───────────────────────────────────────────────

export const fetchBusinessCategories = createAsyncThunk(
  'registration/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('businesscategory');
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load categories');
    }
  }
);

export const fetchPublicPlans = createAsyncThunk(
  'registration/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('subscriptions/plans/public');
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load plans');
    }
  }
);

export const sendOtp = createAsyncThunk(
  'registration/sendOtp',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('registration/send-otp', data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to send OTP');
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'registration/verifyOtp',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('registration/verify-otp', data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Invalid or expired OTP');
    }
  }
);

export const checkEmailAvailable = createAsyncThunk(
  'registration/checkEmail',
  async (email, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(
        `registration/check-email?email=${encodeURIComponent(email)}`
      );
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Error checking email');
    }
  }
);

export const submitStep1 = createAsyncThunk(
  'registration/submitStep1',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('registration/step1', data);
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Registration failed');
    }
  }
);

export const submitStep2 = createAsyncThunk(
  'registration/submitStep2',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('registration/step2', data);
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Profile update failed');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────

const registrationSlice = createSlice({
  name: 'registration',
  initialState: {
    step: 1,
    companyId: null,   // Changed: was registrationId
    userId: null,      // New: track the created user
    token: null,       // signed token returned by step 1
    phoneVerified: false,
    otpSent: false,
    emailAvailable: null,
    categories: [],
    plans: [],
    loading: false,
    otpLoading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetRegistration: (state) => {
      state.step = 1;
      state.companyId = null;
      state.userId = null;
      state.phoneVerified = false;
      state.otpSent = false;
      state.emailAvailable = null;
      state.loading = false;
      state.otpLoading = false;
      state.error = null;
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Categories ──
      .addCase(fetchBusinessCategories.fulfilled, (state, action) => {
        state.categories = action.payload || [];
      })

      // ── Public Plans ──
      .addCase(fetchPublicPlans.fulfilled, (state, action) => {
        state.plans = action.payload || [];
      })

      // ── Send OTP ──
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.otpLoading = false;
        state.otpSent = true;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      })

      // ── Verify OTP ──
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.otpLoading = false;
        state.phoneVerified = true;
        state.step = 2;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      })

      // ── Check Email ──
      .addCase(checkEmailAvailable.fulfilled, (state, action) => {
        state.emailAvailable = action.payload?.available;
      })

      // ── Submit Step 1 ──
      .addCase(submitStep1.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitStep1.fulfilled, (state, action) => {
        state.loading = false;
        // Backend now returns companyId + userId directly
        state.companyId = action.payload?.companyId;
        state.userId    = action.payload?.userId;
        state.token     = action.payload?.token || null;   // signed proof for step 2
        state.step = 3;
      })
      .addCase(submitStep1.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ── Submit Step 2 ──
      .addCase(submitStep2.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitStep2.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(submitStep2.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetRegistration, clearError } = registrationSlice.actions;
export default registrationSlice.reducer;